import React, { useEffect, useState } from 'react';
import { Card, Table, Spin, Empty, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { mocService, MocHeader } from '@/services/moc.service';

const { Text } = Typography;

const statusColors: Record<string, string> = {
  draft: 'default',
  submitted: 'processing',
  under_review: 'processing',
  risk_assessment_pending: 'orange',
  approval_pending: 'gold',
  approved_for_implementation: 'cyan',
  implementation_in_progress: 'blue',
  verification_pending: 'purple',
  closed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

export interface RelatedMocsPanelProps {
  workOrderId?: string;
  projectId?: string;
  supplierId?: string;
  salesOrderId?: string;
  assetId?: string;
}

export const RelatedMocsPanel: React.FC<RelatedMocsPanelProps> = ({
  workOrderId,
  projectId,
  supplierId,
  salesOrderId,
  assetId,
}) => {
  const [rows, setRows] = useState<MocHeader[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = workOrderId
      ? { work_order_id: workOrderId }
      : projectId
        ? { project_id: projectId }
        : supplierId
          ? { supplier_id: supplierId }
          : salesOrderId
            ? { sales_order_id: salesOrderId }
            : assetId
              ? { asset_id: assetId }
              : null;
    if (!params) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const list = await mocService.listRelatedLinks(params);
        if (!cancelled) setRows(list);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workOrderId, projectId, supplierId, salesOrderId, assetId]);

  const columns: ColumnsType<MocHeader> = [
    {
      title: 'MOC #',
      dataIndex: 'moc_number',
      key: 'moc_number',
      width: 140,
    },
    {
      title: 'Title',
      dataIndex: 'change_title',
      key: 'change_title',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (s: string) => (
        <Tag color={statusColors[s] || 'default'}>{s.replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (v: string | undefined) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: '',
      key: 'open',
      width: 80,
      render: (_, r) => (
        <Link to={`/grc/moc/${r.id}`}>Open</Link>
      ),
    },
  ];

  return (
    <Card size="small" title="Related Management of Change (MOC)" style={{ marginTop: 16 }}>
      {loading ? (
        <Spin />
      ) : rows.length === 0 ? (
        <Empty description={<Text type="secondary">No MOCs linked to this record</Text>} />
      ) : (
        <Table<MocHeader>
          rowKey="id"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={rows}
        />
      )}
    </Card>
  );
};
